import _ from 'lodash';

import { AiChatPlanStatus, AiChatPlanStepStatus } from 'constants/aiChat';

const resolveExecutionId = payload =>
  payload?.executionId ?? payload?.stepExecutionId ?? payload?.messageId ?? null;

// DEV-2913: every `plan_step_*` event for the BE's internal "synthesize"
// wrapper step (PlanExecutor.BuildInlinePlan + ChatMessageProcessor's
// final-response emit) is internal scaffolding for the inline ModelTurn plan
// per ADR-0001. The render-time `isVisiblePlanStep` filter already hides it
// at the card level, but if it gets upserted into store BEFORE `plan_created`
// arrives — which happens reliably in production because PlanExecutor's emit
// fires immediately after ChatMessageProcessor enters executor mode and
// races plan_created delivery — the FE briefly renders a "Running:
// inline-execution" row and the placeholder-card branch flips off. Reject
// the event at the reducer level so the synthesize step never touches FE
// state at all. Centralised so every applyPlanStep* reducer can short-circuit
// consistently.
const SYNTHESIS_STEP_KEY = 'synthesize';

const isSynthesisStepEvent = payload => payload?.stepKey === SYNTHESIS_STEP_KEY;

// Defense-in-depth: blob-rehydrated steps may carry status values that don't
// already match our snake_case constants. Older blobs from before the BE added
// `JsonStringEnumConverter(SnakeCaseLower)` to ExecutionPlanJsonOptions emit
// integer ordinals (0..4) or PascalCase ("Pending", "InProgress"); keeping
// these unmapped re-hydrates every completed step as pending and surfaces a
// blue spinner + Cancel button on a finished plan after page refresh.
const STEP_STATUS_ALIASES = {
  pending: AiChatPlanStepStatus.PENDING,
  in_progress: AiChatPlanStepStatus.IN_PROGRESS,
  inprogress: AiChatPlanStepStatus.IN_PROGRESS,
  running: AiChatPlanStepStatus.IN_PROGRESS,
  completed: AiChatPlanStepStatus.COMPLETED,
  complete: AiChatPlanStepStatus.COMPLETED,
  success: AiChatPlanStepStatus.COMPLETED,
  succeeded: AiChatPlanStepStatus.COMPLETED,
  failed: AiChatPlanStepStatus.FAILED,
  failure: AiChatPlanStepStatus.FAILED,
  error: AiChatPlanStepStatus.FAILED,
  skipped: AiChatPlanStepStatus.SKIPPED,
  awaiting_confirmation: AiChatPlanStepStatus.AWAITING_CONFIRMATION,
  awaitingconfirmation: AiChatPlanStepStatus.AWAITING_CONFIRMATION,
  cancelled: AiChatPlanStepStatus.CANCELLED,
  canceled: AiChatPlanStepStatus.CANCELLED,
  // ExecutionPlanStepStatus enum ordinals (BE: Pending=0, InProgress=1,
  // Completed=2, Failed=3, Skipped=4, AwaitingConfirmation=5, Cancelled=6)
  0: AiChatPlanStepStatus.PENDING,
  1: AiChatPlanStepStatus.IN_PROGRESS,
  2: AiChatPlanStepStatus.COMPLETED,
  3: AiChatPlanStepStatus.FAILED,
  4: AiChatPlanStepStatus.SKIPPED,
  5: AiChatPlanStepStatus.AWAITING_CONFIRMATION,
  6: AiChatPlanStepStatus.CANCELLED,
};

const normalizeStepStatus = status => {
  if (_.isNil(status)) {
    return AiChatPlanStepStatus.PENDING;
  }

  if (_.isNumber(status)) {
    return STEP_STATUS_ALIASES[status] ?? AiChatPlanStepStatus.PENDING;
  }

  const key = String(status).trim().toLowerCase();

  return STEP_STATUS_ALIASES[key] ?? AiChatPlanStepStatus.PENDING;
};

const PLAN_STATUS_ALIASES = {
  in_progress: AiChatPlanStatus.IN_PROGRESS,
  inprogress: AiChatPlanStatus.IN_PROGRESS,
  running: AiChatPlanStatus.IN_PROGRESS,
  completed: AiChatPlanStatus.COMPLETED,
  complete: AiChatPlanStatus.COMPLETED,
  success: AiChatPlanStatus.COMPLETED,
  failed: AiChatPlanStatus.FAILED,
  failure: AiChatPlanStatus.FAILED,
  error: AiChatPlanStatus.FAILED,
  cancelled: AiChatPlanStatus.CANCELLED,
  canceled: AiChatPlanStatus.CANCELLED,
};

const normalizePlanStatus = status => {
  if (_.isNil(status)) {
    return null;
  }

  const key = String(status).trim().toLowerCase();

  return PLAN_STATUS_ALIASES[key] ?? null;
};

// When a persisted blob doesn't carry a top-level plan status (the BE
// ExecutionPlan record currently has no Status field), derive it from the
// per-step statuses so the FE can decide whether to render the Cancel button.
// DEV-2913: SKIPPED + CANCELLED are terminal for plan-status purposes —
// a row the LLM diverged from or the user rejected still counts as "done
// enough" for the plan to reach COMPLETED. Without this the plan card
// header would stay stuck on "in progress" forever any time the user
// rejected a navigation card or the model omitted a planner-emitted tool.
const PLAN_STEP_DONE_STATUSES = new Set([
  AiChatPlanStepStatus.COMPLETED,
  AiChatPlanStepStatus.SKIPPED,
  AiChatPlanStepStatus.CANCELLED,
]);

const derivePlanStatusFromSteps = steps => {
  if (!_.isArray(steps) || steps.length === 0) {
    return AiChatPlanStatus.IN_PROGRESS;
  }

  const hasFailed = _.some(steps, step => step.status === AiChatPlanStepStatus.FAILED);

  if (hasFailed) {
    return AiChatPlanStatus.FAILED;
  }

  const allDone = _.every(steps, step => PLAN_STEP_DONE_STATUSES.has(step.status));

  if (allDone) {
    return AiChatPlanStatus.COMPLETED;
  }

  return AiChatPlanStatus.IN_PROGRESS;
};

const findStepIndex = (steps, payload) => {
  if (!_.isNil(payload.stepKey)) {
    const byStepKey = steps.findIndex(step => step.stepKey === payload.stepKey);

    if (byStepKey !== -1) {
      return byStepKey;
    }

    // The BE plan_step_* events use ExecutionPlanStep.Id as `stepKey`, but
    // the upstream plan_created event delivers the same value via `id`. Fall
    // back to id matching so subsequent step events update the existing row
    // instead of inserting a phantom duplicate while streaming.
    const byId = steps.findIndex(step => step.id === payload.stepKey);

    if (byId !== -1) {
      return byId;
    }
  }

  if (!_.isNil(payload.stepIndex)) {
    const idx = steps.findIndex(step => step.stepIndex === payload.stepIndex);

    if (idx !== -1) {
      return idx;
    }
  }

  if (!_.isNil(payload.stepId)) {
    return steps.findIndex(step => step.id === payload.stepId);
  }

  return -1;
};

const normalizeIncomingStep = (step, fallbackIndex) => ({
  stepKey: step.stepKey ?? null,
  stepIndex: step.stepIndex ?? fallbackIndex,
  id: step.id ?? null,
  description: step.description ?? null,
  toolName: step.toolName ?? step.tool ?? null,
  status: normalizeStepStatus(step.status),
  startedAt: step.startedAt ?? null,
  completedAt: step.completedAt ?? null,
  summary: step.summary ?? null,
  error: step.error ?? null,
  kind: step.kind ?? null,
  label: step.label ?? null,
  dependsOn: step.dependsOn ?? [],
  argsPreview: step.argsPreview ?? null,
  outputPreview: step.outputPreview ?? null,
  outputRef: step.outputRef ?? null,
  tokensInput: step.tokensInput ?? null,
  tokensOutput: step.tokensOutput ?? null,
  durationMs: step.durationMs ?? null,
  queuedAt: step.queuedAt ?? null,
  runningAt: step.runningAt ?? null,
  firstTokenAt: step.firstTokenAt ?? null,
  isNewSinceReplan: step.isNewSinceReplan ?? false,
  superseded: step.superseded ?? false,
  supersededByStepId: step.supersededByStepId ?? null,
  recoveryAction: step.recoveryAction ?? null,
  scannedSections: step.scannedSections ?? null,
  toolCalls: _.isArray(step.toolCalls) ? step.toolCalls : [],
});

// The BE fires a distinct plan_step_tool_dispatched / plan_step_tool_returned
// pair for every tool invocation, but a step that calls the same tool N times
// (e.g. get_guideline_section read across N sections) reuses one stepKey, so the
// step-level argsPreview/outputPreview only ever hold the LAST call. Accumulate
// each invocation into a per-step list so the UI can surface every call instead
// of collapsing them. Match a return to its dispatch by toolCallId when present,
// else fill the most recent invocation still missing its output.
const matchToolCallIndex = (toolCalls, payload) => {
  if (!_.isNil(payload.toolCallId)) {
    const byId = toolCalls.findIndex(call => call.toolCallId === payload.toolCallId);

    if (byId !== -1) {
      return byId;
    }
  }

  return _.findLastIndex(toolCalls, call => _.isNil(call.outputPreview));
};

const appendDispatchedToolCall = (toolCalls, payload) => [
  ...toolCalls,
  {
    toolCallId: payload.toolCallId ?? null,
    toolName: payload.toolName ?? payload.tool ?? null,
    argsPreview: payload.argsPreview ?? null,
    outputPreview: null,
    durationMs: null,
  },
];

const fillReturnedToolCall = (toolCalls, payload) => {
  const index = matchToolCallIndex(toolCalls, payload);
  const returned = {
    outputPreview: payload.outputPreview ?? null,
    durationMs: payload.durationMs ?? null,
  };

  if (index === -1) {
    return [
      ...toolCalls,
      {
        toolCallId: payload.toolCallId ?? null,
        toolName: payload.toolName ?? payload.tool ?? null,
        argsPreview: payload.argsPreview ?? null,
        ...returned,
      },
    ];
  }

  const next = [...toolCalls];
  next[index] = { ...next[index], ...returned };

  return next;
};

const createPlanEntry = (executionId, payload) => {
  const normalizedSteps = (payload.steps ?? []).map((step, index) =>
    normalizeIncomingStep(step, index),
  );
  const explicitStatus = normalizePlanStatus(payload.status);
  const status = explicitStatus ?? derivePlanStatusFromSteps(normalizedSteps);

  return {
    executionId,
    messageId: payload.messageId ?? null,
    stepExecutionId: payload.stepExecutionId ?? null,
    planId: payload.planId ?? null,
    status,
    steps: normalizedSteps,
    metadata: payload.metadata ?? null,
    planBlobId: payload.planBlobId ?? null,
    planBlobLoaded: false,
    // `isPlanning` is set by applyPlanPlanningStarted before plan_created
    // arrives, and cleared again by applyPlanCreated. While true (and no
    // steps yet), the FE renders a "Planning..." placeholder card.
    isPlanning: payload.isPlanning === true,
    version: payload.version ?? 1,
    reason: payload.reason ?? null,
  };
};

const ensurePlanEntry = (executionPlans, executionId, payload) => {
  if (executionPlans[executionId]) {
    return executionPlans[executionId];
  }

  return createPlanEntry(executionId, payload);
};

const upsertStep = (plan, payload, changes) => {
  const existingIndex = findStepIndex(plan.steps, payload);

  if (existingIndex === -1) {
    const insertedDescription = payload.description ?? null;
    const resolvedChanges = _.isFunction(changes)
      ? changes(normalizeIncomingStep({}, plan.steps.length))
      : changes;
    const newStep = normalizeIncomingStep(
      {
        stepKey: payload.stepKey ?? null,
        stepIndex: payload.stepIndex,
        id: payload.stepId ?? payload.id ?? null,
        description: insertedDescription,
        toolName: payload.toolName ?? payload.tool ?? null,
        kind: payload.kind ?? null,
        // DEV-2939: never seed a step's label with a value that just echoes
        // its description (see merge-path note below) — leave it null so the
        // adapter synthesizes the short friendly title.
        label:
          payload.label != null && payload.label !== insertedDescription ? payload.label : null,
        dependsOn: payload.dependsOn ?? [],
        ...resolvedChanges,
      },
      plan.steps.length,
    );

    return { ...plan, steps: [...plan.steps, newStep] };
  }

  const nextSteps = [...plan.steps];
  const existing = nextSteps[existingIndex];
  const resolvedChanges = _.isFunction(changes) ? changes(existing) : changes;
  const mergedDescription = payload.description ?? existing.description;
  const merged = {
    ...existing,
    ...resolvedChanges,
    description: mergedDescription,
    toolName: payload.toolName ?? payload.tool ?? existing.toolName,
    kind: payload.kind ?? existing.kind,
    // DEV-2939: the bridged plan_step_started for an advisory chat step sets
    // `label` to the planner's `description` (BE
    // ModelTurnStepHandler.ResolveBridgeLabel). Persisting that into store
    // would make a row's title flip from the short friendly tool label to the
    // long planner sentence the moment the step goes live, diverging from both
    // the pending render and the post-refresh render (the blob carries
    // label = null). Reject an incoming label that merely echoes the
    // description so the adapter keeps synthesizing the short title.
    label:
      payload.label != null && payload.label !== mergedDescription ? payload.label : existing.label,
    dependsOn:
      Array.isArray(payload.dependsOn) && payload.dependsOn.length > 0
        ? payload.dependsOn
        : existing.dependsOn,
  };

  nextSteps[existingIndex] = merged;

  return { ...plan, steps: nextSteps };
};

// Stamps a `isPlanning` placeholder entry into the executionPlans map when
// `plan_planning_started` arrives BEFORE `plan_created`. The placeholder
// has no steps; the ActionPlan component reads `isPlanning` to render a
// "Planning..." card during the 5-10s planner-LLM window.
export const applyPlanPlanningStarted = (executionPlans, payload) => {
  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  // If a real plan entry already exists for this execution (e.g. event
  // arrived out-of-order or after a refresh), don't clobber it.
  const existing = executionPlans[executionId];

  if (existing && Array.isArray(existing.steps) && existing.steps.length > 0) {
    return executionPlans;
  }

  return {
    ...executionPlans,
    [executionId]: createPlanEntry(executionId, { ...payload, isPlanning: true }),
  };
};

export const applyPlanCreated = (executionPlans, payload) => {
  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  // Clearing `isPlanning` happens implicitly: createPlanEntry defaults it
  // to false when the planning flag isn't passed in the payload.
  return {
    ...executionPlans,
    [executionId]: createPlanEntry(executionId, payload),
  };
};

export const applyPlanUpdated = (executionPlans, payload) => {
  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const mergedPlan = {
    ...basePlan,
    status: normalizePlanStatus(payload.status) ?? basePlan.status,
    metadata: _.isNil(payload.metadata) ? basePlan.metadata : payload.metadata,
    planId: payload.planId ?? basePlan.planId,
    planBlobId: payload.planBlobId ?? basePlan.planBlobId,
    version: payload.version ?? basePlan.version,
  };

  return {
    ...executionPlans,
    [executionId]: mergedPlan,
  };
};

export const applyPlanStepStarted = (executionPlans, payload) => {
  if (isSynthesisStepEvent(payload)) {
    return executionPlans;
  }

  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const startedAt = payload.runningAt ?? payload.startedAt ?? new Date().toISOString();
  const updatedPlan = upsertStep(basePlan, payload, {
    status: AiChatPlanStepStatus.IN_PROGRESS,
    startedAt,
    runningAt: payload.runningAt ?? startedAt,
    queuedAt: payload.queuedAt ?? null,
    completedAt: null,
  });

  return {
    ...executionPlans,
    [executionId]: updatedPlan,
  };
};

export const applyPlanStepToolDispatched = (executionPlans, payload) => {
  if (isSynthesisStepEvent(payload)) {
    return executionPlans;
  }

  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const updatedPlan = upsertStep(basePlan, payload, existing => ({
    argsPreview: payload.argsPreview ?? null,
    toolCalls: appendDispatchedToolCall(existing.toolCalls ?? [], payload),
  }));

  return {
    ...executionPlans,
    [executionId]: updatedPlan,
  };
};

export const applyPlanStepToolReturned = (executionPlans, payload) => {
  if (isSynthesisStepEvent(payload)) {
    return executionPlans;
  }

  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const updatedPlan = upsertStep(basePlan, payload, existing => ({
    outputPreview: payload.outputPreview ?? null,
    durationMs: payload.durationMs ?? null,
    toolCalls: fillReturnedToolCall(existing.toolCalls ?? [], payload),
  }));

  return {
    ...executionPlans,
    [executionId]: updatedPlan,
  };
};

export const applyPlanStepCompleted = (executionPlans, payload) => {
  if (isSynthesisStepEvent(payload)) {
    return executionPlans;
  }

  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const completedAt = payload.completedAt ?? new Date().toISOString();
  const isFailed = payload.status === AiChatPlanStepStatus.FAILED;
  const status = isFailed ? AiChatPlanStepStatus.FAILED : AiChatPlanStepStatus.COMPLETED;
  const updatedPlan = upsertStep(basePlan, payload, {
    status,
    completedAt,
    queuedAt: payload.queuedAt ?? null,
    runningAt: payload.runningAt ?? null,
    firstTokenAt: payload.firstTokenAt ?? null,
    durationMs: payload.durationMs ?? null,
    tokensInput: payload.tokensInput ?? null,
    tokensOutput: payload.tokensOutput ?? null,
    outputRef: payload.outputRef ?? null,
    summary: payload.summary ?? null,
    ...(isFailed ? { error: payload.error ?? null } : {}),
  });

  return {
    ...executionPlans,
    [executionId]: updatedPlan,
  };
};

export const applyPlanStepFailed = (executionPlans, payload) => {
  if (isSynthesisStepEvent(payload)) {
    return executionPlans;
  }

  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const completedAt = payload.completedAt ?? new Date().toISOString();
  const updatedPlan = upsertStep(basePlan, payload, {
    status: AiChatPlanStepStatus.FAILED,
    completedAt,
    queuedAt: payload.queuedAt ?? null,
    runningAt: payload.runningAt ?? null,
    firstTokenAt: payload.firstTokenAt ?? null,
    error: payload.error ?? null,
    recoveryAction: payload.recoveryAction ?? null,
  });

  return {
    ...executionPlans,
    [executionId]: updatedPlan,
  };
};

// Marks a planner-emitted advisory step as SKIPPED. Fires for every step
// the LLM never invoked during execution (planner expected the tool; the
// model chose a different path). Wire shape carries no timing info because
// the step never ran — we stamp completedAt = now so post-mortem renderers
// have a finite timestamp.
export const applyPlanStepSkipped = (executionPlans, payload) => {
  if (isSynthesisStepEvent(payload)) {
    return executionPlans;
  }

  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const completedAt = new Date().toISOString();
  const updatedPlan = upsertStep(basePlan, payload, {
    status: AiChatPlanStepStatus.SKIPPED,
    completedAt,
  });

  return {
    ...executionPlans,
    [executionId]: updatedPlan,
  };
};

// DEV-2913: bridged advisory step entered the "awaiting user confirmation"
// state — BE handed off to StreamingToolExecutor which fired the FE
// confirmation card. The row gets a distinct AWAITING_CONFIRMATION badge
// (rendered by the ActionPlan adapter) until the resume turn flips it to
// COMPLETED or CANCELLED.
export const applyPlanStepAwaitingConfirmation = (executionPlans, payload) => {
  if (isSynthesisStepEvent(payload)) {
    return executionPlans;
  }

  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const requestedAt = payload.requestedAt ?? new Date().toISOString();
  const updatedPlan = upsertStep(basePlan, payload, {
    status: AiChatPlanStepStatus.AWAITING_CONFIRMATION,
    runningAt: payload.queuedAt ?? requestedAt,
    queuedAt: payload.queuedAt ?? null,
    // Stamp the friendly tool name onto the step so the adapter can render
    // the right icon even before plan_step_tool_dispatched fires.
    toolName: payload.tool ?? payload.toolName ?? null,
  });

  return {
    ...executionPlans,
    [executionId]: updatedPlan,
  };
};

// DEV-2913: user rejected the FE confirmation card. Flip to CANCELLED
// with a muted badge — distinct from FAILED (no error) and SKIPPED (model
// never invoked the tool — here the model did, the user just said no).
export const applyPlanStepCancelled = (executionPlans, payload) => {
  if (isSynthesisStepEvent(payload)) {
    return executionPlans;
  }

  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId)) {
    return executionPlans;
  }

  const basePlan = ensurePlanEntry(executionPlans, executionId, payload);
  const completedAt = new Date().toISOString();
  const updatedPlan = upsertStep(basePlan, payload, {
    status: AiChatPlanStepStatus.CANCELLED,
    completedAt,
    // Stash the reason as an error field so the row can surface it on
    // hover / expansion (existing pattern from applyPlanStepFailed).
    error: payload.reason ?? null,
  });

  return {
    ...executionPlans,
    [executionId]: updatedPlan,
  };
};

export const applyPlanCompleted = (executionPlans, payload) => {
  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId) || !executionPlans[executionId]) {
    return executionPlans;
  }

  const basePlan = executionPlans[executionId];
  const completedAt = new Date().toISOString();

  // Resolve any step left non-terminal at plan completion. The resolution
  // depends on whether the step ever started:
  //
  //   - PENDING -> SKIPPED. A step still PENDING here was never invoked:
  //     `plan_step_started` never fired, so the model diverged from the
  //     planner and never ran the predicted tool (e.g. asked to edit income,
  //     the model never called `get_assets`). Normally `plan_step_skipped`
  //     marks these, but when that event doesn't arrive this sweep must NOT
  //     paint a green completion check on work that never happened (DEV-2951).
  //
  //   - IN_PROGRESS / AWAITING_CONFIRMATION -> COMPLETED. These DID run or are
  //     mid-flight: a non-tool model-turn step has no explicit completion
  //     event, and a bridged frontend-routed advisory step
  //     (e.g. open_loan_section) the user approved can be missing its
  //     resume-turn `plan_step_completed` because ChatMessageProcessor's
  //     resume branch does not rehydrate `_planContext`
  //     (`FindBridgedAdvisoryStepIdForResume` returns null) — see DEV-2918.
  //     Flipping these to COMPLETED closes the loop visually instead of
  //     leaving a stuck spinner / "Awaiting" badge forever.
  //
  // Returns null for already-terminal steps (COMPLETED/FAILED/SKIPPED/
  // CANCELLED), which are left untouched.
  const resolveOpenStatus = step => {
    if (step.status === AiChatPlanStepStatus.PENDING) {
      return AiChatPlanStepStatus.SKIPPED;
    }

    if (
      step.status === AiChatPlanStepStatus.IN_PROGRESS ||
      step.status === AiChatPlanStepStatus.AWAITING_CONFIRMATION
    ) {
      return AiChatPlanStepStatus.COMPLETED;
    }

    return null;
  };

  const nextSteps = basePlan.steps.some(step => resolveOpenStatus(step) !== null)
    ? basePlan.steps.map(step => {
        const resolvedStatus = resolveOpenStatus(step);

        return resolvedStatus === null
          ? step
          : {
              ...step,
              status: resolvedStatus,
              completedAt: step.completedAt ?? completedAt,
            };
      })
    : basePlan.steps;

  return {
    ...executionPlans,
    [executionId]: {
      ...basePlan,
      status: AiChatPlanStatus.COMPLETED,
      steps: nextSteps,
    },
  };
};

export const applyPlanCancelled = (executionPlans, payload) => {
  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId) || !executionPlans[executionId]) {
    return executionPlans;
  }

  const basePlan = executionPlans[executionId];

  return {
    ...executionPlans,
    [executionId]: {
      ...basePlan,
      status: AiChatPlanStatus.CANCELLED,
      reason: payload.reason ?? basePlan.reason ?? null,
    },
  };
};

export const applyPlanRevised = (executionPlans, payload) => {
  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId) || !executionPlans[executionId]) {
    return executionPlans;
  }

  const basePlan = executionPlans[executionId];
  const newStepIds = new Set(payload.newStepIds ?? []);
  const supersededStepIds = new Set(payload.supersededStepIds ?? []);
  const supersededByMap = payload.supersededByMap ?? {};

  const nextSteps = basePlan.steps.map(step => {
    const stepKey = step.stepKey ?? step.id;
    const isNew = newStepIds.has(stepKey);
    const isSuperseded = supersededStepIds.has(stepKey);

    if (!isNew && !isSuperseded) {
      return step;
    }

    return {
      ...step,
      isNewSinceReplan: isNew ? true : step.isNewSinceReplan,
      superseded: isSuperseded ? true : step.superseded,
      supersededByStepId: isSuperseded
        ? (supersededByMap[stepKey] ?? step.supersededByStepId)
        : step.supersededByStepId,
    };
  });

  return {
    ...executionPlans,
    [executionId]: {
      ...basePlan,
      steps: nextSteps,
      version: payload.version ?? basePlan.version,
      reason: payload.reason ?? basePlan.reason,
      planId: payload.planId ?? basePlan.planId,
    },
  };
};

export const applyClearPlanRevisionFlags = (executionPlans, payload) => {
  const executionId = resolveExecutionId(payload);

  if (_.isNil(executionId) || !executionPlans[executionId]) {
    return executionPlans;
  }

  const basePlan = executionPlans[executionId];
  const hasFlags = basePlan.steps.some(step => step.isNewSinceReplan);

  if (!hasFlags) {
    return executionPlans;
  }

  return {
    ...executionPlans,
    [executionId]: {
      ...basePlan,
      steps: basePlan.steps.map(step =>
        step.isNewSinceReplan ? { ...step, isNewSinceReplan: false } : step,
      ),
    },
  };
};

export { resolveExecutionId };
